import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Trophy, Medal, TrendingUp, Calendar, DollarSign, Users, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Competitions = () => {
  const navigate = useNavigate();
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const res = await axios.get(`${API}/competitions/active`);
      setCompetitions(res.data.competitions);
    } catch (error) {
      console.error('Error fetching competitions:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const enterCompetition = async (competitionId) => {
    try {
      await axios.post(
        `${API}/competitions/${competitionId}/enter`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Successfully entered competition!');
      navigate(`/competitions/${competitionId}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to enter competition');
    }
  };

  const getPrizeColor = (index) => {
    if (index === 0) return 'text-yellow-400';
    if (index === 1) return 'text-gray-300';
    if (index === 2) return 'text-orange-400';
    return 'text-indigo-400';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#050505] via-[#0a0a0f] to-[#050505] text-white">
      <nav className="glass border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <h1 className="text-2xl font-bold">Competitions</h1>
          <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30 text-xs">TESTNET</Badge>
          <div className="flex-1" />
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>Dashboard</Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-yellow-600/10 rounded-full mb-4">
            <Trophy className="h-12 w-12 text-yellow-400" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">Win Real Prizes</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Compete with test tokens, win real USD prizes. Test your DeFi skills risk-free!
          </p>
        </div>

        {competitions.length === 0 ? (
          <Card className="bg-white/5 border-white/10 max-w-2xl mx-auto">
            <CardContent className="py-12 text-center">
              <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-bold mb-2">No Active Competitions</h3>
              <p className="text-gray-400 mb-4">Check back soon for new competitions!</p>
              <Button variant="outline" onClick={() => navigate('/academy')}>Explore Academy</Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {competitions.map((comp) => (
              <Card key={comp.id} className="bg-white/5 border-white/10 hover:border-yellow-500/50 transition-all">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-2xl mb-2">{comp.title}</CardTitle>
                      <CardDescription>{comp.description}</CardDescription>
                    </div>
                    {comp.type === 'weekly' && <Badge className="bg-blue-500/20 text-blue-300">WEEKLY</Badge>}
                    {comp.type === 'monthly' && <Badge className="bg-purple-500/20 text-purple-300">MONTHLY</Badge>}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-secondary rounded-lg">
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Prize Pool</p>
                      <p className="text-2xl font-bold text-green-400">${comp.prize_pool}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400 mb-1">Participants</p>
                      <p className="text-2xl font-bold">{comp.participants || 0}</p>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-400">Ends: {new Date(comp.end_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Users className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-400">{comp.tier_required === 'free' ? 'Open to all' : `${comp.tier_required} tier required`}</span>
                    </div>
                  </div>

                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-sm font-semibold mb-2">🏆 Prize Breakdown:</p>
                    <div className="space-y-1 text-sm">
                      {comp.prizes?.map((prize, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span className={getPrizeColor(idx)}>
                            {idx === 0 ? '🥇' : idx === 1 ? '🥈' : idx === 2 ? '🥉' : '🎖️'} {prize.position}
                          </span>
                          <span className="text-green-400 font-semibold">${prize.amount}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button 
                    className="w-full bg-yellow-600 hover:bg-yellow-700"
                    onClick={() => enterCompetition(comp.id)}
                  >
                    Enter Competition <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Card className="mt-12 bg-gradient-to-r from-yellow-600/20 to-orange-600/20 border-yellow-500/30">
          <CardContent className="py-8">
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <Medal className="h-12 w-12 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">Practice Trading</h3>
                <p className="text-gray-400">Use test tokens to compete</p>
              </div>
              <div>
                <DollarSign className="h-12 w-12 text-green-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">Win Real Money</h3>
                <p className="text-gray-400">Top performers get USD prizes</p>
              </div>
              <div>
                <TrendingUp className="h-12 w-12 text-indigo-400 mx-auto mb-3" />
                <h3 className="text-xl font-bold mb-2">Build Skills</h3>
                <p className="text-gray-400">Learn from the best traders</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Competitions;